import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Separator } from '../../../../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../../../../components/ui/avatar';
import { ArrowLeft, Receipt, Calendar, User, DollarSign, FileText, RefreshCw, AlertCircle, CreditCard, ClipboardList, CheckCircle2, Circle, Phone, Mail, Clock, XCircle, Check, Dot, Building, IndianRupee } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../../hooks/useApi';
import { Skeleton } from '../../../../components/ui/skeleton';

const ViewDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get } = useApi();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const statusBadgeClasses = {
    paid: "bg-green-100 text-green-700 border border-green-200",
    draft: "bg-orange-100 text-orange-700 border border-orange-200",
    cancelled: "bg-red-100 text-red-700 border border-red-200",
    cancel: "bg-red-100 text-red-700 border border-red-200",
    approved: "bg-green-100 text-green-700 border border-green-200",
    sent : "bg-blue-100 text-blue-700 border border-blue-200",
  };

  const getStatusBadgeClass = (status) => {
    const normalizedStatus = (status || "").toLowerCase();
    return (
      statusBadgeClasses[normalizedStatus] ||
      "bg-muted text-muted-foreground border border-muted-foreground/30"
    );
  };

  useEffect(() => {
    if (id) {
      fetchInvoiceDetails();
    }
  }, [id]);

  const fetchInvoiceDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Try different API endpoints
      let response;
      try {
        response = await get(`/invoices/${id}`);
      } catch (firstError) {
        try {
          response = await get(`/admin/invoices/${id}`);
        } catch (secondError) {
          response = await get(`/invoices/${id}/details`);
        }
      }
      
      
      // Handle different response structures
      if (response?.success && response?.data) {
        setInvoice(response.data);
      } else if (response?.data) {
        setInvoice(response.data);
      } else if (Array.isArray(response) && response.length > 0) {
        setInvoice(response[0]);
      } else if (response && typeof response === 'object' && response.id) {
        // Direct invoice object response
        setInvoice(response);
      } else {
        throw new Error('Invoice not found');
      }
    } catch (error) {
      setError('Failed to fetch invoice details');
      toast.error('Failed to fetch invoice details');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    await fetchInvoiceDetails();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', label: 'Draft' },
      sent: { variant: 'default', label: 'Sent' },
      paid: { variant: 'default', label: 'Paid' },
      overdue: { variant: 'destructive', label: 'Overdue' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getRandomAvatar = () => {
    // Generate random 3D avatars from different services
    const avatars = [
      // DiceBear 3D-style avatars (these work as direct image URLs)
      'https://plus.unsplash.com/premium_vector-1717511155337-6ed3d51bc2d8?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Njl8fGludm9pY2V8ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&q=60&w=900',

    ];
    const randomIndex = Math.floor(Math.random() * avatars.length);
    return avatars[randomIndex];
  };

  const handleAvatarError = () => {
    // Handle avatar error
  };

  const goBack = () => {
    navigate('/admin/invoices');
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-3 space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-10 w-24" />
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6">
          {/* Loading skeleton for all sections */}
          <Card className="hidden lg:block lg:col-span-3 border-1 border-muted-foreground/20 shadow-lg  bg-muted/70">
            <CardHeader>
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-1/2 space-y-6">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-5 h-5 rounded" />
                    <Skeleton className="h-6 w-32" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="space-y-3">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-5 w-32" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-l border-gray-500 pl-9"></div>
                <div className="lg:w-1/2 space-y-6">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-5 h-5 rounded" />
                    <Skeleton className="h-6 w-32" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="space-y-3">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-5 w-32" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Other skeleton cards */}
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-1 border-muted-foreground/20 shadow-lg  bg-muted/70">
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500 text-lg">{error}</p>
          <Button variant="outline" className="mt-4" onClick={goBack}>
            Back to Invoices
          </Button>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground text-lg">Invoice not found</p>
          <Button variant="outline" className="mt-4" onClick={goBack}>
            Back to Invoices
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-3 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Invoice Details</h1>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshData}
          disabled={isLoading}
          className="flex items-center gap-2 text-foreground"
        >
          <RefreshCw className={`w-4 h-4 text-foreground ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4">
        {/* Desktop Layout */}
        <Card className="hidden lg:block lg:col-span-3 border-1 border-muted-foreground/20  bg-muted/70">
          <CardHeader>
            <CardTitle>
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Invoice Details */}
                <div className="lg:w-1/2 space-y-6">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    <h3 className="text-lg font-semibold">Invoice Details</h3>  
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Invoice Number</p>
                      <p className="font-medium text-base">{invoice.invoice_number || 'N/A'}</p>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Issue Date</p>
                      <p className="font-medium text-base">{formatDate(invoice.issue_date)}</p>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Due Date</p>
                      <p className="font-medium text-base">{formatDate(invoice.due_date)}</p>
                    </div>
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge
                      className={`w-fit text-xs font-semibold px-3 py-1 rounded-full ${getStatusBadgeClass(
                        invoice.status
                      )}`}
                    >
                      {invoice.status || "N/A"}
                    </Badge>
                  </div>
                  </div>
                </div>r

                <div className="border-l border-gray-500 pl-9"></div>

                {/* Order Details */}
                <div className="lg:w-1/2 space-y-6">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-6 h-6" />
                    <h3 className="text-lg font-semibold">Order Details</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Subtotal</p>
                      <p className="font-medium text-base">{formatCurrency(invoice.subtotal || 0)}</p>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Total Tax</p>
                      <p className="font-medium text-base">{formatCurrency(invoice.total_tax || 0)}</p>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">GST Type</p>
                      <p className="font-medium capitalize text-base">{invoice.gst_type || 'N/A'}</p>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Total Amount</p>
                      <p className="font-medium text-green-600 text-base">{formatCurrency(invoice.total || 0)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
        </Card>

        {/* quote Details Card */}
        <Card className="border-1 border-muted-foreground/20  bg-muted/70 overflow-hidden">
          <CardHeader className="rounded-t-lg border-b py-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-2  bg-muted/70/20 rounded-full">
                <User className="w-4 h-4" />
              </div>
              Quote Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-6">
              {/* Customer Avatar and Basic Info */}
              <div className="flex items-start gap-4">
                <div className="relative">
                  <Avatar className="h-14 w-14 ring-4 ring-background shadow-lg">
                    <AvatarImage
                      src={getRandomAvatar()}
                      alt="Customer Avatar"
                      onError={handleAvatarError}
                    />
                    <AvatarFallback className="bg-muted text-muted-foreground">
                      <User className="w-6 h-6" />
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="text-xl font-bold text-foreground">
                    {invoice.quote?.quote_number || 'Quote'}
                  </h3>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                    <Badge
                      variant="outline"
                      className="px-3 my-2 py-1 text-xs bg-background border-border text-foreground"
                    >
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      Active Quote
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Customer Details List */}
              <div className="space-y-4">
                
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Notes</span>
                  <span className="text-sm font-medium">{invoice.quote?.notes || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Quote Status</span>
                  <span className="text-sm font-medium capitalize">{invoice.quote?.status || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Created Date</span>
                  <span className="text-sm font-medium capitalize">{formatDate(invoice.quote?.created_at || 'N/A')}</span>
                </div>
                
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Summary Card */}
        <Card className="border-1 border-muted-foreground/20  bg-muted/70 overflow-hidden">
          <CardHeader className="rounded-t-lg border-b py-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-2  bg-muted/70/20 rounded-full">
                <Receipt className="w-4 h-4" />
              </div>
              Invoice Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="relative">
                  <div className="h-14 w-14 ring-4 ring-background shadow-lg bg-primary/10 rounded-full flex items-center justify-center">
                    <IndianRupee className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="text-base font-bold text-foreground">
                    {invoice.invoice_number || 'N/A'}
                  </h3>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                    <Badge
                      variant="outline"
                      className="px-3 my-2 py-1 text-xs bg-background border-border text-foreground"
                    >
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      {invoice.status || 'Generated'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Issue Date</span>
                  <span className="text-sm font-medium">{formatDate(invoice.issue_date)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Due Date</span>
                  <span className="text-sm font-medium">{formatDate(invoice.due_date)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="text-sm font-medium text-blue-600">{formatCurrency(invoice.subtotal || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Total Tax</span>
                  <span className="text-sm font-medium text-orange-600">{formatCurrency(invoice.total_tax || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Total Amount</span>
                  <span className="text-sm font-medium text-green-600 font-semibold">{formatCurrency(invoice.total || 0)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Details Card */}
        <Card className="border-1 border-muted-foreground/20  bg-muted/70 overflow-hidden">
          <CardHeader className="rounded-t-lg border-b py-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-2  bg-muted/70/20 rounded-full">
                <ClipboardList className="w-4 h-4" />
              </div>
              Additional Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Quote Number</span>
                  <span className="text-sm font-medium">{invoice.quote?.quote_number || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">GST Type</span>
                  <span className="text-sm font-medium capitalize">{invoice.gst_type || 'N/A'}</span>
                </div>
                {invoice.gst_type === 'intra' && (
                  <>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">CGST Rate</span>
                      <span className="text-sm font-medium">{invoice.cgst_rate}%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">SGST Rate</span>
                      <span className="text-sm font-medium">{invoice.sgst_rate}%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">CGST Amount</span>
                      <span className="text-sm font-medium">{formatCurrency(invoice.cgst_amount || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">SGST Amount</span>
                      <span className="text-sm font-medium">{formatCurrency(invoice.sgst_amount || 0)}</span>
                    </div>
                  </>
                )}
                {invoice.gst_type === 'inter' && (
                  <>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">IGST Rate</span>
                      <span className="text-sm font-medium">{invoice.igst_rate}%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">IGST Amount</span>
                      <span className="text-sm font-medium">{formatCurrency(invoice.igst_amount || 0)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items Card */}
        <Card className="border-1 border-muted-foreground/20  bg-muted/70 w-full lg:col-span-3">
          <CardHeader className="rounded-t-lg border-b py-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4" />
              Invoice Items
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-6">
              {invoice.items && invoice.items.length > 0 ? (
                <div className="space-y-4">
                  {invoice.items.map((item, index) => (
                    <div key={index} className="bg-muted/30 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Item {index + 1}</span>
                        <span className="text-sm font-medium text-green-600">{formatCurrency(item.total_price || 0)}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Description</span>
                        <span className="text-sm font-medium">{item.description || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Quantity</span>
                        <span className="text-sm font-medium">{item.quantity || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Unit Price</span>
                        <span className="text-sm font-medium">{formatCurrency(item.unit_price || 0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No items found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ViewDetails;